/**
 * Attaching notes, journal entries and lists to the work they are about.
 *
 * A note lives in a folder, but the reason it exists is usually a project, a
 * task or a habit — so each of the three "written" kinds carries an array of
 * ids per target kind, and attaching is membership in that array. There is no
 * join table and no back-reference: the holder owns the link, one direction
 * only, which is what makes detaching a single array write.
 *
 * The membership rules lived inline in `AttachedItemsSection`, where nothing
 * else could reach them, and that is the whole reason the assistant could not
 * attach anything: not a missing feature so much as a feature with exactly one
 * door. Extracted here so the app and the connector attach the same way — the
 * same reason `withCaptureDefaults` and `applyProjectUpdate` live in core.
 */
export const LINK_FIELD_BY_TARGET = Object.freeze({
    goal: 'linked_goal_ids',
    project: 'linked_project_ids',
    habit: 'linked_habit_ids',
    task: 'linked_task_ids',
});
export const LINK_TARGET_TYPES = Object.freeze(['goal', 'project', 'habit', 'task']);
/**
 * A list has no `linked_task_ids` — lists attach to goals, projects and habits
 * only. That is a real gap in the model rather than an oversight to route
 * around: writing the field anyway would store something no reader looks at,
 * so callers must be told no instead.
 */
export function canLink(holder, target) {
    return !(holder === 'list' && target === 'task');
}
/** Whatever is currently attached, tolerating data written before the field existed. */
export function linksOf(holder, field) {
    const value = holder[field];
    return Array.isArray(value) ? value : [];
}
export function isLinkedTo(holder, field, targetId) {
    return linksOf(holder, field).includes(targetId);
}
/**
 * The array to store after attaching or detaching.
 *
 * Returns the SAME array contents when there is nothing to do, so a caller can
 * skip a pointless write. Attaching is idempotent — a second attach of the
 * same target must not produce a duplicate id, because a duplicate would show
 * the attachment twice and take two detaches to clear.
 */
export function withLink(current, targetId, attach) {
    const links = current ?? [];
    const present = links.includes(targetId);
    if (attach)
        return present ? [...links] : [...links, targetId];
    return present ? links.filter((id) => id !== targetId) : [...links];
}
//# sourceMappingURL=links.js.map